import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Download, Trash2, Plus, Search, Save, Users, ChevronDown, ChevronRight } from 'lucide-react';
import * as adminService from '../services/adminService';

const PROVIDERS = ['aws', 'gcp', 'azure', 'runpod', 'vast', 'lambda_cloud', 'paperspace'];

// Providers that offer both community and datacenter hardware
const DUAL_TIER_PROVIDERS = new Set(['vast', 'runpod']);

const normalizeGpuName = (name) => (name || '').replace(/\s+/g, '');

const SOURCE_TIER_BADGE = {
  community: 'bg-blue-100 text-blue-700',
  datacenter: 'bg-purple-100 text-purple-700',
  all: 'bg-gray-100 text-gray-600',
};

const AdminGPUConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [refreshingCatalog, setRefreshingCatalog] = useState(false);
  const [catalogMsg, setCatalogMsg] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Edit state for inline editing
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Override form
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    user_id: '', provider: '', accelerator_name: '', access_granted: true,
    markup_percent: '', fixed_price_per_hour: '', notes: ''
  });
  const [savingOverride, setSavingOverride] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [configRes, overrideRes, catalogRes] = await Promise.allSettled([
        adminService.getGpuConfigs(),
        adminService.getGpuOverrides(),
        adminService.getAcceleratorCatalog(),
      ]);
      if (configRes.status === 'fulfilled' && configRes.value.success) setConfigs(configRes.value.configs || []);
      if (overrideRes.status === 'fulfilled' && overrideRes.value.success) setOverrides(overrideRes.value.overrides || []);
      if (catalogRes.status === 'fulfilled' && catalogRes.value.success) setCatalog(catalogRes.value);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique provider+GPU+source_tier combos from catalog that aren't in configs yet
  // For dual-tier providers (Vast, RunPod), always create both community + datacenter entries
  const unconfiguredGpus = useMemo(() => {
    if (!catalog?.rows) return [];
    const configuredKeys = new Set(configs.map(c => `${c.provider}:${normalizeGpuName(c.accelerator_name)}:${c.source_tier}`));
    const configuredAllKeys = new Set(configs.filter(c => c.source_tier === 'all').map(c => `${c.provider}:${normalizeGpuName(c.accelerator_name)}`));
    const seen = new Set();
    const result = [];
    for (const row of catalog.rows) {
      const gpu = normalizeGpuName(row.gpu);
      const providerGpu = `${row.provider}:${gpu}`;
      if (configuredAllKeys.has(providerGpu)) continue;

      if (DUAL_TIER_PROVIDERS.has(row.provider)) {
        for (const tier of ['community', 'datacenter']) {
          const key = `${providerGpu}:${tier}`;
          if (!configuredKeys.has(key) && !seen.has(key)) {
            seen.add(key);
            result.push({ provider: row.provider, accelerator_name: gpu, source_tier: tier });
          }
        }
      } else {
        const sourceTier = row.datacenter ? 'datacenter' : 'community';
        const key = `${providerGpu}:${sourceTier}`;
        if (!configuredKeys.has(key) && !seen.has(key)) {
          seen.add(key);
          result.push({ provider: row.provider, accelerator_name: gpu, source_tier: sourceTier });
        }
      }
    }
    return result.sort((a, b) => a.provider.localeCompare(b.provider) || a.accelerator_name.localeCompare(b.accelerator_name) || a.source_tier.localeCompare(b.source_tier));
  }, [catalog, configs]);

  // Build catalog stats per provider+GPU+source_tier (normalized GPU names)
  const catalogStats = useMemo(() => {
    if (!catalog?.rows) return {};
    const map = {};
    for (const row of catalog.rows) {
      const gpu = normalizeGpuName(row.gpu);
      const sourceTier = row.datacenter ? 'datacenter' : 'community';
      const key = `${row.provider}:${gpu}:${sourceTier}`;
      if (!map[key]) map[key] = { prices: [], spotPrices: [], regions: new Set(), instances: 0 };
      map[key].instances += 1;
      if (row.price > 0) map[key].prices.push(row.price);
      if (row.spot_price > 0) map[key].spotPrices.push(row.spot_price);
      if (row.region) map[key].regions.add(row.region);

      const allKey = `${row.provider}:${gpu}:all`;
      if (!map[allKey]) map[allKey] = { prices: [], spotPrices: [], regions: new Set(), instances: 0 };
      map[allKey].instances += 1;
      if (row.price > 0) map[allKey].prices.push(row.price);
      if (row.spot_price > 0) map[allKey].spotPrices.push(row.spot_price);
      if (row.region) map[allKey].regions.add(row.region);
    }
    const result = {};
    for (const [key, data] of Object.entries(map)) {
      const sorted = [...data.prices].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      result[key] = {
        count: data.instances,
        regions: data.regions.size,
        min: sorted.length > 0 ? sorted[0] : null,
        max: sorted.length > 0 ? sorted[sorted.length - 1] : null,
        median: sorted.length > 0
          ? (sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid])
          : null,
        spotMin: data.spotPrices.length > 0 ? Math.min(...data.spotPrices) : null,
      };
    }
    return result;
  }, [catalog]);

  const filteredConfigs = useMemo(() => {
    return configs.filter(c => {
      if (providerFilter && c.provider !== providerFilter) return false;
      if (search && !c.accelerator_name.toLowerCase().includes(search.toLowerCase()) && !c.provider.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [configs, providerFilter, search]);

  const handleImportFromCatalog = async () => {
    if (unconfiguredGpus.length === 0) return;
    if (!window.confirm(`Import ${unconfiguredGpus.length} GPU configs from catalog? Community and datacenter GPUs will be imported as separate entries.`)) return;
    setImporting(true);
    try {
      const importConfigs = unconfiguredGpus.map(g => ({
        provider: g.provider,
        accelerator_name: g.accelerator_name,
        source_tier: g.source_tier,
        tier_community: true,
        tier_datacenter: true,
        enabled: true,
      }));
      const res = await adminService.bulkUpsertGpuConfigs(importConfigs);
      if (res.success) {
        await fetchAll();
      } else {
        alert(res.error || 'Import failed');
      }
    } catch (err) {
      alert('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleToggle = async (config, field) => {
    try {
      const res = await adminService.upsertGpuConfig({
        ...config,
        [field]: !config[field],
      });
      if (res.success) {
        setConfigs(prev => prev.map(c =>
          c.id === config.id ? { ...c, [field]: !config[field] } : c
        ));
      }
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const startEdit = (config) => {
    setEditingId(config.id);
    setEditForm({
      markup_percent: config.markup_percent ?? '',
      fixed_price_per_hour: config.fixed_price_per_hour ?? '',
      notes: config.notes || '',
    });
  };

  const handleSaveEdit = async (config) => {
    try {
      const res = await adminService.upsertGpuConfig({
        ...config,
        markup_percent: editForm.markup_percent === '' ? null : parseFloat(editForm.markup_percent),
        fixed_price_per_hour: editForm.fixed_price_per_hour === '' ? null : parseFloat(editForm.fixed_price_per_hour),
        notes: editForm.notes || null,
      });
      if (res.success) {
        setEditingId(null);
        await fetchAll();
      } else {
        alert(res.error || 'Save failed');
      }
    } catch (err) {
      alert('Save failed');
    }
  };

  const handleDeleteConfig = async (id) => {
    if (!window.confirm('Delete this GPU config?')) return;
    try {
      const res = await adminService.deleteGpuConfig(id);
      if (res.success) setConfigs(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      alert('Delete failed');
    }
  };

  const handleDeleteAllConfigs = async () => {
    if (!window.confirm(`Delete all ${configs.length} GPU configs? This cannot be undone.`)) return;
    try {
      const res = await adminService.deleteAllGpuConfigs();
      if (res.success) {
        setConfigs([]);
        await fetchAll();
      } else {
        alert(res.error || 'Delete all failed');
      }
    } catch (err) {
      alert('Delete all failed');
    }
  };

  const handleSaveOverride = async (e) => {
    e.preventDefault();
    setSavingOverride(true);
    try {
      const res = await adminService.upsertGpuOverride({
        user_id: parseInt(overrideForm.user_id),
        provider: overrideForm.provider,
        accelerator_name: overrideForm.accelerator_name,
        access_granted: overrideForm.access_granted,
        markup_percent: overrideForm.markup_percent === '' ? null : parseFloat(overrideForm.markup_percent),
        fixed_price_per_hour: overrideForm.fixed_price_per_hour === '' ? null : parseFloat(overrideForm.fixed_price_per_hour),
        notes: overrideForm.notes || null,
      });
      if (res.success) {
        setShowOverrideForm(false);
        setOverrideForm({ user_id: '', provider: '', accelerator_name: '', access_granted: true, markup_percent: '', fixed_price_per_hour: '', notes: '' });
        await fetchAll();
      } else {
        alert(res.error || 'Save failed');
      }
    } catch (err) {
      alert('Save failed');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleDeleteOverride = async (id) => {
    if (!window.confirm('Delete this user override?')) return;
    try {
      const res = await adminService.deleteGpuOverride(id);
      if (res.success) setOverrides(prev => prev.filter(o => o.id !== id));
    } catch (err) {
      alert('Delete failed');
    }
  };

  // Summary stats
  const communityCount = configs.filter(c => c.enabled && c.tier_community).length;
  const datacenterCount = configs.filter(c => c.enabled && c.tier_datacenter).length;

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading GPU configuration...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">GPU Configuration</h2>
        <div className="flex items-center space-x-2">
          {catalogMsg && (
            <span className={`text-xs px-2 py-1 rounded ${catalogMsg.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {catalogMsg.text}
            </span>
          )}
          <button
            onClick={async () => {
              setRefreshingCatalog(true);
              setCatalogMsg(null);
              try {
                const res = await adminService.refreshAcceleratorCatalog();
                if (res.success) {
                  setCatalogMsg({ ok: true, text: `Loaded ${res.rows?.length || 0} instances from ${Object.keys(res.providers || {}).length} providers` });
                  await fetchAll();
                } else {
                  setCatalogMsg({ ok: false, text: res.error || 'Refresh failed' });
                }
              } catch (err) {
                setCatalogMsg({ ok: false, text: err.message || 'Network error' });
              }
              setRefreshingCatalog(false);
            }}
            disabled={refreshingCatalog}
            className="text-sm px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50 flex items-center space-x-1"
          >
            <RefreshCw size={14} className={refreshingCatalog ? 'animate-spin' : ''} />
            <span>{refreshingCatalog ? 'Fetching from providers...' : 'Refresh Catalog'}</span>
          </button>
          <button onClick={fetchAll} className="text-gray-500 hover:text-gray-700 p-2">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Total Configured</p>
          <p className="text-2xl font-bold text-gray-900">{configs.length}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Community Tier</p>
          <p className="text-2xl font-bold text-blue-600">{communityCount}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Datacenter Tier</p>
          <p className="text-2xl font-bold text-purple-600">{datacenterCount}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">User Overrides</p>
          <p className="text-2xl font-bold text-orange-600">{overrides.length}</p>
        </div>
      </div>

      {/* GPU Config Table */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">GPU Availability & Pricing</h3>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text" placeholder="Search..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border rounded-lg w-48"
              />
            </div>
            <select
              value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}
              className="text-sm border rounded-lg px-3 py-2"
            >
              <option value="">All Providers</option>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {configs.length > 0 && (
              <button
                onClick={handleDeleteAllConfigs}
                className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-700 flex items-center space-x-1"
              >
                <Trash2 size={14} />
                <span>Delete All</span>
              </button>
            )}
            {unconfiguredGpus.length > 0 && (
              <button
                onClick={handleImportFromCatalog} disabled={importing}
                className="bg-purple-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-1"
              >
                <Download size={14} />
                <span>{importing ? 'Importing...' : `Import ${unconfiguredGpus.length} from Catalog`}</span>
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="text-left px-4 py-2 text-xs text-gray-500">Provider</th>
                <th className="text-left px-4 py-2 text-xs text-gray-500">GPU</th>
                <th className="text-center px-4 py-2 text-xs text-gray-500">Source</th>
                <th className="text-center px-4 py-2 text-xs text-gray-500">Community</th>
                <th className="text-center px-4 py-2 text-xs text-gray-500">Datacenter</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500">Markup %</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500">Fixed $/hr</th>
                <th className="text-center px-4 py-2 text-xs text-gray-500">Enabled</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredConfigs.map(c => {
                const normName = normalizeGpuName(c.accelerator_name);
                const tierStats = catalogStats[`${c.provider}:${normName}:${c.source_tier || 'all'}`];
                const allStats = catalogStats[`${c.provider}:${normName}:all`];
                const stats = tierStats || allStats;
                const isFallbackStats = !tierStats && !!allStats;
                const isExpanded = expandedRow === c.id;
                return (<React.Fragment key={c.id}>
                <tr
                  className={`hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
                  onClick={() => setExpandedRow(isExpanded ? null : c.id)}
                >
                  <td className="px-2 py-2 text-gray-400">
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </td>
                  <td className="px-4 py-2 font-medium">{c.provider}</td>
                  <td className="px-4 py-2">{c.accelerator_name}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_TIER_BADGE[c.source_tier] || SOURCE_TIER_BADGE.all}`}>
                      {c.source_tier || 'all'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleToggle(c, 'tier_community')}
                      className={`w-8 h-5 rounded-full transition-colors ${c.tier_community ? 'bg-blue-500' : 'bg-gray-300'}`}
                    >
                      <span className={`block w-3.5 h-3.5 bg-white rounded-full transition-transform ${c.tier_community ? 'translate-x-3.5 ml-0.5' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleToggle(c, 'tier_datacenter')}
                      className={`w-8 h-5 rounded-full transition-colors ${c.tier_datacenter ? 'bg-purple-500' : 'bg-gray-300'}`}
                    >
                      <span className={`block w-3.5 h-3.5 bg-white rounded-full transition-transform ${c.tier_datacenter ? 'translate-x-3.5 ml-0.5' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right" onClick={e => e.stopPropagation()}>
                    {editingId === c.id ? (
                      <input
                        type="number" step="0.1" className="w-20 border rounded px-2 py-1 text-sm text-right"
                        value={editForm.markup_percent} placeholder="global"
                        onChange={(e) => setEditForm(p => ({ ...p, markup_percent: e.target.value }))}
                      />
                    ) : (
                      <span className={c.markup_percent != null ? 'font-mono' : 'text-gray-400'}>
                        {c.markup_percent != null ? `${c.markup_percent}%` : 'global'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right" onClick={e => e.stopPropagation()}>
                    {editingId === c.id ? (
                      <input
                        type="number" step="0.01" className="w-20 border rounded px-2 py-1 text-sm text-right"
                        value={editForm.fixed_price_per_hour} placeholder="—"
                        onChange={(e) => setEditForm(p => ({ ...p, fixed_price_per_hour: e.target.value }))}
                      />
                    ) : (
                      <span className={c.fixed_price_per_hour != null ? 'font-mono text-green-600' : 'text-gray-400'}>
                        {c.fixed_price_per_hour != null ? `$${c.fixed_price_per_hour}` : '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleToggle(c, 'enabled')}
                      className={`w-8 h-5 rounded-full transition-colors ${c.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`block w-3.5 h-3.5 bg-white rounded-full transition-transform ${c.enabled ? 'translate-x-3.5 ml-0.5' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end space-x-1">
                      {editingId === c.id ? (
                        <>
                          <button onClick={() => handleSaveEdit(c)} className="text-green-600 hover:text-green-800 p-1"><Save size={14} /></button>
                          <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600 p-1 text-xs">Cancel</button>
                        </>
                      ) : (
                        <button onClick={() => startEdit(c)} className="text-blue-600 hover:text-blue-800 p-1 text-xs">Edit</button>
                      )}
                      <button onClick={() => handleDeleteConfig(c.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-blue-50">
                    <td colSpan="10" className="px-6 py-3">
                      {stats ? (
                        <div className="flex items-center space-x-6 text-xs text-gray-600">
                          {isFallbackStats && <span className="text-amber-600 font-medium">Combined stats</span>}
                          <span><span className="font-semibold text-gray-800">{stats.count}</span> instances</span>
                          <span><span className="font-semibold text-gray-800">{stats.regions}</span> regions</span>
                          {stats.min != null && (
                            <>
                              <span>Min <span className="font-mono font-semibold text-green-700">{'$'}{stats.min.toFixed(2)}</span>/hr</span>
                              <span>Median <span className="font-mono font-semibold text-blue-700">{'$'}{stats.median.toFixed(2)}</span>/hr</span>
                              <span>Max <span className="font-mono font-semibold text-red-700">{'$'}{stats.max.toFixed(2)}</span>/hr</span>
                            </>
                          )}
                          {stats.spotMin != null && (
                            <span>Spot from <span className="font-mono font-semibold text-purple-700">{'$'}{stats.spotMin.toFixed(2)}</span>/hr</span>
                          )}
                          {!stats.min && <span className="text-gray-400">No pricing data</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No catalog data available for this GPU</span>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>);
              })}
              {filteredConfigs.length === 0 && (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    {configs.length === 0 ? 'No GPU configs yet. Import from the accelerator catalog to get started.' : 'No matching configs.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Overrides */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
            <Users size={18} />
            <span>User Overrides ({overrides.length})</span>
          </h3>
          <button
            onClick={() => setShowOverrideForm(!showOverrideForm)}
            className="bg-purple-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-purple-700 flex items-center space-x-1"
          >
            <Plus size={14} />
            <span>Add Override</span>
          </button>
        </div>

        {showOverrideForm && (
          <form onSubmit={handleSaveOverride} className="p-4 bg-gray-50 border-b space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">User ID</label>
                <input
                  type="number" required value={overrideForm.user_id}
                  onChange={(e) => setOverrideForm(p => ({ ...p, user_id: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="User ID"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Provider</label>
                <select
                  required value={overrideForm.provider}
                  onChange={(e) => setOverrideForm(p => ({ ...p, provider: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Select...</option>
                  {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">GPU</label>
                <input
                  type="text" required value={overrideForm.accelerator_name}
                  onChange={(e) => setOverrideForm(p => ({ ...p, accelerator_name: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g. H100"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Access</label>
                <select
                  value={overrideForm.access_granted ? 'true' : 'false'}
                  onChange={(e) => setOverrideForm(p => ({ ...p, access_granted: e.target.value === 'true' }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="true">Granted</option>
                  <option value="false">Denied</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Custom Markup %</label>
                <input
                  type="number" step="0.1" value={overrideForm.markup_percent}
                  onChange={(e) => setOverrideForm(p => ({ ...p, markup_percent: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Fixed Price $/hr</label>
                <input
                  type="number" step="0.01" value={overrideForm.fixed_price_per_hour}
                  onChange={(e) => setOverrideForm(p => ({ ...p, fixed_price_per_hour: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  type="text" value={overrideForm.notes}
                  onChange={(e) => setOverrideForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="Optional"
                />
              </div>
              <div className="flex items-end">
                <button type="submit" disabled={savingOverride} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 w-full">
                  {savingOverride ? 'Saving...' : 'Save Override'}
                </button>
              </div>
            </div>
          </form>
        )}

        {overrides.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">User</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Provider</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">GPU</th>
                  <th className="text-center px-4 py-2 text-xs text-gray-500">Access</th>
                  <th className="text-right px-4 py-2 text-xs text-gray-500">Markup %</th>
                  <th className="text-right px-4 py-2 text-xs text-gray-500">Fixed $/hr</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Notes</th>
                  <th className="text-right px-4 py-2 text-xs text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {overrides.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <span className="font-medium">{o.user_email || o.user_username}</span>
                      <span className="text-gray-400 ml-1">#{o.user_id}</span>
                    </td>
                    <td className="px-4 py-2">{o.provider}</td>
                    <td className="px-4 py-2">{o.accelerator_name}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.access_granted ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {o.access_granted ? 'Granted' : 'Denied'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {o.markup_percent != null ? `${o.markup_percent}%` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {o.fixed_price_per_hour != null ? `$${o.fixed_price_per_hour}` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{o.notes || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleDeleteOverride(o.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {overrides.length === 0 && !showOverrideForm && (
          <div className="p-8 text-center text-gray-500 text-sm">
            No user overrides configured. Add one to give a specific user custom GPU access or pricing.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGPUConfigPage;
